import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { PropsWithChildren } from "react";

const DEFAULT_PREFIX = "Data/TrackingFolder";

export type BlulogMinioReport = {
  prefix?: string;
  bucket?: string;
  endpoint?: string;
  secure?: boolean;
};

export type BlulogDataSources = {
  prefix: string;
  tableKey: string;
  countriesKey: string;
  articlesKey: string;
  refreshVersion: number;
  minioReport: BlulogMinioReport | null;
};

export type BlulogExtractionSummary = {
  processedIds: string[];
  finishedAt?: Date;
  message?: string;
};

type BlulogContextValue = {
  dataSources: BlulogDataSources;
  updateFromMinio: (report: BlulogMinioReport) => void;
  markDataRefreshed: () => void;
  resetDataSources: () => void;
  lastExtraction: BlulogExtractionSummary | null;
  setLastExtraction: (summary: BlulogExtractionSummary | null) => void;
};

const DEFAULT_DATA_SOURCES: BlulogDataSources = {
  prefix: DEFAULT_PREFIX,
  tableKey: "table.csv",
  countriesKey: "countries.csv",
  articlesKey: "articles.csv",
  refreshVersion: 0,
  minioReport: null,
};

const BlulogContext = createContext<BlulogContextValue | undefined>(undefined);

function normalisePrefix(value?: string | null) {
  if (!value) return null;
  return value.replace(/^[/]+|\/+$/g, "");
}

export function BlulogProvider({ children }: PropsWithChildren) {
  const [dataSources, setDataSources] = useState<BlulogDataSources>(DEFAULT_DATA_SOURCES);
  const [lastExtraction, setLastExtraction] = useState<BlulogExtractionSummary | null>(null);

  const updateFromMinio = useCallback((report: BlulogMinioReport) => {
    setDataSources((previous) => {
      const nextPrefix = normalisePrefix(report.prefix) ?? previous.prefix;
      return {
        ...previous,
        prefix: nextPrefix || DEFAULT_PREFIX,
        minioReport: { ...previous.minioReport, ...report },
      };
    });
  }, []);

  const markDataRefreshed = useCallback(() => {
    setDataSources((previous) => ({
      ...previous,
      refreshVersion: previous.refreshVersion + 1,
    }));
  }, []);

  const resetDataSources = useCallback(() => {
    setDataSources(DEFAULT_DATA_SOURCES);
  }, []);

  const value = useMemo<BlulogContextValue>(
    () => ({
      dataSources,
      updateFromMinio,
      markDataRefreshed,
      resetDataSources,
      lastExtraction,
      setLastExtraction,
    }),
    [dataSources, lastExtraction, markDataRefreshed, resetDataSources, updateFromMinio],
  );

  return <BlulogContext.Provider value={value}>{children}</BlulogContext.Provider>;
}

export function useBlulogContext() {
  const context = useContext(BlulogContext);
  if (!context) {
    throw new Error("useBlulogContext must be used within a BlulogProvider");
  }
  return context;
}

export function useBlulogDataSources() {
  return useBlulogContext().dataSources;
}

export function useBlulogExtraction() {
  const { lastExtraction, setLastExtraction } = useBlulogContext();
  return { lastExtraction, setLastExtraction };
}
